// AcaComplianceCheckPanel — blueprint module #5 ACA Compliance AI.
//
// Mounts inside HealthClientProfileModal. On mount + on-demand:
//   1. Calls POST /api/aca/compliance-check with the client_id
//   2. Renders the deterministic issue list (severe / warning / info)
//   3. Prominently flags "This application may violate Marketplace
//      requirements" per blueprint copy when severe issues exist
//   4. Shows the specific remediation for each issue so the agent
//      knows the next click, not just what's wrong
//
// Auth: attaches the operator's Supabase JWT so RLS on the endpoint
// scopes correctly.

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { supabase } from '../lib/supabase';

async function apiFetch(path, opts = {}) {
  let jwt = null;
  jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    ...opts,
    headers: {
      'content-type': 'application/json',
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
      ...(opts.headers || {}),
    },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
  return body;
}

const SEVERITY_STYLE = {
  severe:  { border: 'border-rose-300',   bg: 'bg-rose-50',    text: 'text-rose-900',   badge: 'bg-rose-600 text-white',   icon: '✕', label: 'SEVERE' },
  warning: { border: 'border-amber-300',  bg: 'bg-amber-50',   text: 'text-amber-900',  badge: 'bg-amber-500 text-white',  icon: '⚑', label: 'WARN' },
  info:    { border: 'border-sky-300',    bg: 'bg-sky-50',     text: 'text-sky-900',    badge: 'bg-sky-500 text-white',    icon: '◆', label: 'INFO' },
};

export default function AcaComplianceCheckPanel({ clientId }) {
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState(null);
  const [result, setResult] = React.useState(null);
  const [refreshedAt, setRefreshedAt] = React.useState(null);

  const run = React.useCallback(async () => {
    if (!clientId) { setLoading(false); return; }
    setLoading(true); setErr(null);
    try {
      const r = await apiFetch('/api/aca/compliance-check', {
        method: 'POST',
        body: JSON.stringify({ client_id: clientId }),
      });
      setResult(r);
      setRefreshedAt(new Date());
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  React.useEffect(() => { run(); }, [run]);

  if (loading && !result) {
    return <div className="text-xs text-slate-500 italic px-1 py-2">Running ACA compliance check…</div>;
  }
  if (err) {
    return (
      <div className="text-xs text-rose-700 italic px-1 py-2">
        ACA compliance check failed: {err}
        <button onClick={run} className="ml-2 underline">Retry</button>
      </div>
    );
  }
  if (!result) return null;

  const { safe_to_submit, issues, scope, llm_check } = result;
  const severe = issues.filter(i => i.severity === 'severe');
  const warnings = issues.filter(i => i.severity === 'warning');
  const infos = issues.filter(i => i.severity === 'info');

  const headerTone = safe_to_submit && issues.length === 0
    ? 'from-emerald-50 to-white border-emerald-300 text-emerald-900'
    : safe_to_submit
      ? 'from-amber-50 to-white border-amber-300 text-amber-900'
      : 'from-rose-50 to-white border-rose-300 text-rose-900';

  return (
    <div className="space-y-3">
      {/* Header + banner */}
      <div className={`border-2 rounded-xl px-4 py-3 bg-gradient-to-b ${headerTone}`}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold opacity-70">ACA Compliance AI</div>
            <div className="text-lg font-black mt-0.5">
              {safe_to_submit && issues.length === 0
                ? 'Clear to submit'
                : safe_to_submit
                  ? `${warnings.length + infos.length} advisory notes — safe to submit`
                  : `${severe.length} severe issue${severe.length === 1 ? '' : 's'} · block submission`}
            </div>
            {!safe_to_submit && (
              <div className="text-[11.5px] mt-1 font-semibold">
                ⚑ This application may violate Marketplace requirements.
              </div>
            )}
          </div>
          <div className="text-right">
            <button
              onClick={run}
              disabled={loading}
              className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 hover:text-slate-900 disabled:opacity-40"
            >
              {loading ? 'Re-checking…' : '↻ Re-check'}
            </button>
            {refreshedAt && (
              <div className="text-[10px] opacity-60 mt-0.5">
                Checked {refreshedAt.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
              </div>
            )}
          </div>
        </div>
        <div className="mt-3 text-[10.5px] opacity-70">
          Scope: {scope.policies_checked} policy record{scope.policies_checked === 1 ? '' : 's'} checked
          {scope.subsidy_snapshot_age_days != null && ` · subsidy snapshot ${scope.subsidy_snapshot_age_days}d old`}
        </div>
      </div>

      {/* Issue list — severe first, then warnings, then info */}
      {issues.length === 0 ? (
        <div className="text-[12px] text-slate-600 italic px-1">No rule violations. Enrollment can proceed.</div>
      ) : (
        <div className="space-y-2">
          {[...severe, ...warnings, ...infos].map((issue, i) => {
            const style = SEVERITY_STYLE[issue.severity] || SEVERITY_STYLE.info;
            return (
              <div key={i} className={`border rounded-lg ${style.border} ${style.bg} ${style.text} p-3`}>
                <div className="flex items-start gap-2">
                  <span className={`inline-flex items-center gap-1 text-[9.5px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded ${style.badge}`}>
                    {style.icon} {style.label}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] font-bold">{issue.label}</div>
                    <div className="text-[11.5px] mt-1 opacity-90 leading-snug">{issue.detail}</div>
                    <div className="text-[11px] mt-2 flex items-baseline gap-1.5">
                      <span className="font-bold uppercase tracking-wider opacity-70">Fix</span>
                      <span>{issue.remediation}</span>
                    </div>
                    <div className="text-[9.5px] mt-1.5 opacity-50 font-mono">{issue.code}{issue.field ? ` · ${issue.field}` : ''}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer note about LLM step being deferred */}
      {llm_check?.status === 'not_run' && (
        <div className="text-[10.5px] text-slate-500 italic px-1">
          Deterministic-rules pass only. LLM cross-check for cross-field consistency + fraud patterns is a scoped follow-up.
        </div>
      )}
    </div>
  );
}
